import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { BookOpen, ChevronDown, ChevronRight, CheckCircle2, Plus } from 'lucide-react';
import { useAuth, useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', teal: '#0E7C7B', amber: '#8A6D1D', line: '#E4E9EF' };
const STATUS = { draft: { bg: '#FDF6E3', fg: '#8A6D1D' }, approved: { bg: '#E8F4EC', fg: '#1B7A3D' }, superseded: { bg: '#EEF1F5', fg: '#5C6B7A' } };

function PackDetail({ packId, layers, api }) {
  const [pack, setPack] = useState(null);
  useEffect(() => {
    api.get(`/knowledge/packs/${packId}`).then(r => setPack(r.data)).catch(() => {});
  }, [packId, api]);
  if (!pack) return <div className="p-3 text-xs" style={{ color: C.slate }}>Loading pack…</div>;
  return (
    <div className="p-4 space-y-3 border-t" style={{ borderColor: C.line, background: '#FAFBFC' }} data-testid={`pack-detail-${packId}`}>
      {Object.entries(layers).map(([lid, label]) => {
        const entries = pack.layers?.[lid] || [];
        return (
          <div key={lid}>
            <div className="text-[11px] font-bold tracking-wide" style={{ color: C.navy }}>{lid} — {label} <span style={{ color: C.slate }}>({entries.length})</span></div>
            {entries.length === 0
              ? <div className="text-[11px] italic ml-4" style={{ color: C.slate }}>empty</div>
              : entries.map(e => (
                <div key={e.entry_id} className="ml-4 mt-1 p-2 rounded border text-xs" style={{ borderColor: C.line, background: '#fff' }} data-testid={`pack-entry-${e.entry_id}`}>
                  <div style={{ color: C.navy }}>
                    {e.ref?.exemplar && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded mr-1.5 align-middle" style={{ background: '#0E7C7B', color: '#fff' }}>EXEMPLAR{e.ref?.decision ? ` · ${e.ref.decision}` : ''}</span>}
                    {e.text}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-[10px]" style={{ color: C.slate }}>
                    <span className="px-1.5 py-0.5 rounded" style={{ background: '#EEF1F5' }}>source: {e.source}</span>
                    <span className="px-1.5 py-0.5 rounded" style={{ background: '#EEF1F5' }}>vintage: {e.vintage}</span>
                    <span className="px-1.5 py-0.5 rounded" style={{ background: '#EEF1F5' }}>by {e.added_by}</span>
                  </div>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}

export default function KnowledgePacksPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [packs, setPacks] = useState([]);
  const [meta, setMeta] = useState({ layers: {}, lines: [] });
  const [open, setOpen] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newLine, setNewLine] = useState('');
  const [newSegment, setNewSegment] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([api.get('/knowledge/packs'), api.get('/knowledge/layers')]);
      setPacks(p.data.packs); setMeta(m.data);
    } catch { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const createPack = async () => {
    if (!newLine) return toast.error('Choose a line');
    try {
      await api.post('/knowledge/packs', { line: newLine, segment: newSegment || null });
      toast.success(`Pack created for ${newLine}`);
      setShowCreate(false); setNewLine(''); setNewSegment(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const approve = async (packId) => {
    try {
      await api.post(`/knowledge/packs/${packId}/approve`);
      toast.success('Pack approved'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  return (
    <div className="space-y-4" data-testid="knowledge-packs-page">
      <div className="flex items-center justify-between">
        <div className="text-xs" style={{ color: C.slate }}>
          Container core (7 Sep 2026). Layers L0–L6; Layer-4 write path live (AFCA tagger + manual); segment overlays live (base + segment returned together); L6 worked exemplars cite precedent. Editing interface follows in Week 2.
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ background: C.navy }} data-testid="create-pack-btn">
            <Plus size={13} /> New Pack
          </button>
        )}
      </div>

      {showCreate && (
        <div className="p-4 rounded-lg border flex flex-wrap items-end gap-3" style={{ borderColor: C.line, background: '#fff' }} data-testid="create-pack-form">
          <div>
            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Line</div>
            <select value={newLine} onChange={e => setNewLine(e.target.value)} className="border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="create-pack-line-select">
              <option value="">Select line…</option>
              {meta.lines.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slate }}>Segment (optional)</div>
            <input value={newSegment} onChange={e => setNewSegment(e.target.value)} placeholder="e.g. hospitality" className="border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="create-pack-segment-input" />
          </div>
          <button onClick={createPack} className="px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ background: C.teal }} data-testid="create-pack-submit-btn">Create Draft Pack</button>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        {packs.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: C.slate }} data-testid="packs-empty">
            <BookOpen size={28} className="mx-auto mb-2 opacity-40" />
            No packs yet — Tier 1 packs land Friday. Confirming an AFCA determination auto-creates the pack for its line.
          </div>
        )}
        {packs.map(p => (
          <div key={p.pack_id} className="border-b last:border-b-0" style={{ borderColor: C.line }}>
            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={() => setOpen(open === p.pack_id ? null : p.pack_id)} data-testid={`pack-row-${p.pack_id}`}>
              {open === p.pack_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="text-sm font-semibold" style={{ color: C.navy }}>{p.line}</span>
              {p.segment
                ? <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#EAF1FB', color: '#2A6FC4' }}>{p.segment} overlay</span>
                : p.channel
                  ? <span className="text-xs px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(108,75,181,0.1)', color: '#6C4BB5' }}>{p.channel} channel</span>
                  : <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: '#EEF1F5', color: C.slate }}>BASE</span>}
              <span className="text-xs" style={{ color: C.slate }}>v{p.version}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase" style={{ background: STATUS[p.status]?.bg, color: STATUS[p.status]?.fg }}>{p.status}</span>
              <span className="ml-auto text-[11px]" style={{ color: C.slate }}>
                {Object.entries(p.entry_counts || {}).filter(([, n]) => n > 0).map(([l, n]) => `${l}:${n}`).join(' · ') || 'no entries'}
              </span>
              {isAdmin && p.status === 'draft' && (
                <button onClick={(e) => { e.stopPropagation(); approve(p.pack_id); }} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-white" style={{ background: C.green }} data-testid={`approve-pack-${p.pack_id}`}>
                  <CheckCircle2 size={11} /> Approve
                </button>
              )}
            </div>
            {open === p.pack_id && <PackDetail packId={p.pack_id} layers={meta.layers} api={api} />}
          </div>
        ))}
      </div>
    </div>
  );
}
